import React, { useState, useEffect, useCallback, useRef } from 'react';
import { X, RefreshCw, Upload, Download, ChevronDown, ChevronRight, AlertTriangle, Check, Loader2, ArrowUp, ArrowDown } from 'lucide-react';

const API_BASE = '/.netlify/functions';

// ============================================
// SYNC PANEL - Modal overlay for private exchange sync
// ============================================
const SyncPanel = ({ isOpen, onClose, getValidAccessToken, onSyncComplete }) => {
  const [phase, setPhase] = useState('scanning'); // scanning | preview | executing | complete
  const [syncPlan, setSyncPlan] = useState(null);
  const [storageInfo, setStorageInfo] = useState(null);
  const [error, setError] = useState(null);
  const [executionProgress, setExecutionProgress] = useState(null);
  const [executionResults, setExecutionResults] = useState(null);

  // Per-workflow conflict resolutions: { [listingId]: { [filePath]: 'keep_local' | 'keep_cloud' | 'keep_both' } }
  const [conflictResolutions, setConflictResolutions] = useState({});

  // Expanded workflow details
  const [expandedWorkflows, setExpandedWorkflows] = useState(new Set());

  // Warnings from scanning (e.g. folders that couldn't be read)
  const [scanWarnings, setScanWarnings] = useState([]);

  // Track new listing IDs created during execution (for cleanup if cancelled)
  const pendingNewListingIds = useRef([]);

  // Start scanning when panel opens
  useEffect(() => {
    if (isOpen) {
      setPhase('scanning');
      setError(null);
      setSyncPlan(null);
      setExecutionProgress(null);
      setExecutionResults(null);
      setConflictResolutions({});
      setExpandedWorkflows(new Set());
      scanAndCompare();
    }
  }, [isOpen]);

  const scanAndCompare = useCallback(async () => {
    try {
      setPhase('scanning');
      setError(null);
      setScanWarnings([]);
      console.log('[Sync] Starting scan...');

      // 1. Get local workflow manifests from Electron
      if (!window.electronAPI?.listLocalWorkflows || !window.electronAPI?.getWorkflowManifest) {
        setError('Sync is only available in the desktop app.');
        return;
      }

      const { folders } = await window.electronAPI.listLocalWorkflows();
      console.log(`[Sync] Found ${folders.length} local folders`);

      // Build manifests for each local folder
      const localManifests = {};
      const warnings = [];
      for (const folder of folders) {
        try {
          const manifest = await window.electronAPI.getWorkflowManifest(folder.name);
          localManifests[folder.name] = { files: manifest.files };
          console.log(`[Sync] Manifest for ${folder.name}: ${manifest.files.length} files`);
        } catch (err) {
          console.error(`[Sync] Failed to scan ${folder.name}:`, err);
          warnings.push(`Could not read "${folder.name}": ${err.message || 'Unknown error'}`);
        }
      }
      setScanWarnings(warnings);

      console.log(`[Sync] Sending ${Object.keys(localManifests).length} manifests to backend`);

      // 2. Send to backend for comparison
      const accessToken = await getValidAccessToken();
      const response = await fetch(`${API_BASE}/marketplace-sync-status`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`
        },
        body: JSON.stringify({ localManifests })
      });

      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        console.error('[Sync] sync-status error:', response.status, errData);
        throw new Error(errData.error || `Server error (${response.status})`);
      }

      const data = await response.json();
      console.log('[Sync] Sync plan received:', {
        paired: data.syncPlan?.paired?.length || 0,
        localOnly: data.syncPlan?.localOnly?.length || 0,
        cloudOnly: data.syncPlan?.cloudOnly?.length || 0,
        storageUsed: data.storageUsed
      });

      setSyncPlan(data.syncPlan);
      setStorageInfo({
        used: data.storageUsed,
        limit: data.storageLimit,
        free: data.storageFree
      });
      setPhase('preview');

    } catch (err) {
      console.error('[Sync] Scan error:', err);
      setError(err.message || 'Failed to scan workflows');
    }
  }, [getValidAccessToken]);

  // Resolve a conflict for a specific file
  const resolveConflict = (listingId, filePath, resolution) => {
    setConflictResolutions(prev => ({
      ...prev,
      [listingId]: {
        ...(prev[listingId] || {}),
        [filePath]: resolution
      }
    }));
  };

  // Toggle expanded state for a workflow
  const toggleExpanded = (id) => {
    setExpandedWorkflows(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  // Check if a paired workflow has unresolved conflicts
  const hasUnresolvedConflicts = (paired) => {
    const conflicts = paired.actions.filter(a => a.action === 'conflict');
    if (conflicts.length === 0) return false;
    const resolutions = conflictResolutions[paired.listingId] || {};
    return conflicts.some(c => !resolutions[c.path]);
  };

  // Execute sync for a single workflow (upload direction)
  const executeUpload = async (paired) => {
    await executeSyncOperations([paired], 'upload');
  };

  // Execute sync for a single workflow (download direction)
  const executeDownload = async (paired) => {
    await executeSyncOperations([paired], 'download');
  };

  // Upload a local-only workflow to the cloud
  const executeUploadNew = async (localOnly) => {
    await executeSyncOperations([], 'upload', [localOnly], []);
  };

  // Download a cloud-only workflow to local
  const executeDownloadNew = async (cloudOnly) => {
    await executeSyncOperations([], 'download', [], [cloudOnly]);
  };

  // Upload all
  const executeUploadAll = async () => {
    const paired = syncPlan?.paired?.filter(p => p.summary.upload > 0 || p.summary.conflict > 0) || [];
    const localOnly = syncPlan?.localOnly || [];
    await executeSyncOperations(paired, 'upload', localOnly, []);
  };

  // Download all
  const executeDownloadAll = async () => {
    const paired = syncPlan?.paired?.filter(p => p.summary.download > 0 || p.summary.conflict > 0) || [];
    const cloudOnly = syncPlan?.cloudOnly || [];
    await executeSyncOperations(paired, 'download', [], cloudOnly);
  };

  // Core sync execution
  const executeSyncOperations = async (pairedItems, direction, newLocalItems = [], newCloudItems = []) => {
    try {
      setPhase('executing');
      setError(null);
      console.log('[Sync] Executing sync:', { paired: pairedItems.length, direction, newLocal: newLocalItems.length, newCloud: newCloudItems.length });

      const operations = [];
      const progress = { total: 0, completed: 0, current: '', failed: [] };

      // Build operations from paired workflows
      for (const paired of pairedItems) {
        const resolutions = conflictResolutions[paired.listingId] || {};

        const uploadFiles = [];
        const downloadFiles = [];

        for (const action of paired.actions) {
          if (action.action === 'upload' && direction === 'upload') {
            uploadFiles.push({ path: action.path, size: action.localSize || 0 });
          } else if (action.action === 'download' && direction === 'download') {
            downloadFiles.push({ path: action.path });
          } else if (action.action === 'conflict') {
            const resolution = resolutions[action.path];
            if (resolution === 'keep_local' || (resolution === 'keep_both' && direction === 'upload')) {
              uploadFiles.push({ path: action.path, size: action.localSize || 0 });
            }
            if (resolution === 'keep_cloud' || (resolution === 'keep_both' && direction === 'download')) {
              const dlPath = resolution === 'keep_both' ? action.path + '.cloud-backup' : action.path;
              downloadFiles.push({ path: action.path, localPath: dlPath });
            }
          }
        }

        if (uploadFiles.length > 0) {
          operations.push({ type: 'upload', listingId: paired.listingId, localFolder: paired.localFolder, files: uploadFiles });
          progress.total += uploadFiles.length;
        }
        if (downloadFiles.length > 0) {
          operations.push({ type: 'download', listingId: paired.listingId, localFolder: paired.localFolder, files: downloadFiles });
          progress.total += downloadFiles.length;
        }
      }

      // New local workflows to upload
      for (const local of newLocalItems) {
        // Get manifest for the local folder
        let manifest;
        try {
          manifest = await window.electronAPI.getWorkflowManifest(local.localFolder);
        } catch {
          progress.failed.push({ path: local.localFolder, error: 'Failed to read local files' });
          continue;
        }
        const op = {
          type: 'create_listing',
          localFolder: local.localFolder,
          files: manifest.files.map(f => ({ path: f.path, size: f.size }))
        };
        // Pass thumbnail file and icon metadata if available
        if (manifest.metadata) {
          if (manifest.metadata.imageFile) op.thumbnailFile = manifest.metadata.imageFile;
          if (manifest.metadata.icon) op.iconName = manifest.metadata.icon;
          if (manifest.metadata.iconWeight) op.iconWeight = manifest.metadata.iconWeight;
          if (manifest.metadata.color) op.iconColor = manifest.metadata.color;
        }
        operations.push(op);
        progress.total += manifest.files.length;
      }

      // Cloud workflows to clone locally
      for (const cloud of newCloudItems) {
        operations.push({ type: 'clone_to_local', listingId: cloud.listingId });
        progress.total += cloud.fileCount || 1;
      }

      if (operations.length === 0) {
        setError('No operations to perform.');
        setPhase('preview');
        return;
      }

      setExecutionProgress({ ...progress });

      // 1. Get presigned URLs from backend
      const accessToken = await getValidAccessToken();
      const execResponse = await fetch(`${API_BASE}/marketplace-sync-execute`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`
        },
        body: JSON.stringify({ operations })
      });

      if (!execResponse.ok) {
        const errData = await execResponse.json().catch(() => ({}));
        throw new Error(errData.message || errData.error || `Server error (${execResponse.status})`);
      }

      const execResult = await execResponse.json();
      console.log('[Sync] sync-execute result:', {
        uploadUrls: execResult.uploadUrls?.length || 0,
        downloadUrls: execResult.downloadUrls?.length || 0,
        newListings: execResult.newListings?.length || 0,
        cloneDownloads: execResult.cloneDownloads?.length || 0
      });

      // Track new listing IDs so we can clean up S3 if user closes mid-sync
      pendingNewListingIds.current = (execResult.newListings || []).map(n => n.listingId);

      // 2. Execute file transfers

      // Upload files to S3
      for (const uploadUrl of (execResult.uploadUrls || [])) {
        try {
          progress.current = `Uploading ${uploadUrl.path}...`;
          setExecutionProgress({ ...progress });

          // Read file content from local
          const op = operations.find(o => o.listingId === uploadUrl.listingId && o.type === 'upload');
          const folderName = op?.localFolder;
          if (folderName) {
            const fileData = await window.electronAPI.readWorkflowFile(folderName, uploadUrl.path);
            // Upload to S3 using presigned URL
            const uploadResp = await fetch(uploadUrl.url, {
              method: 'PUT',
              body: Uint8Array.from(atob(fileData.content), c => c.charCodeAt(0)),
              headers: { 'Content-Type': 'application/octet-stream' }
            });
            if (!uploadResp.ok) throw new Error(`Upload failed: ${uploadResp.status}`);
          }
          progress.completed++;
        } catch (err) {
          progress.failed.push({ path: uploadUrl.path, error: err.message });
          progress.completed++;
        }
        setExecutionProgress({ ...progress });
      }

      // Upload files for new listings
      for (const newListing of (execResult.newListings || [])) {
        const folderName = newListing.localFolder;
        for (const uploadUrl of (newListing.uploadUrls || [])) {
          try {
            progress.current = `Uploading ${folderName}/${uploadUrl.path}...`;
            setExecutionProgress({ ...progress });

            const fileData = await window.electronAPI.readWorkflowFile(folderName, uploadUrl.path);
            const uploadResp = await fetch(uploadUrl.url, {
              method: 'PUT',
              body: Uint8Array.from(atob(fileData.content), c => c.charCodeAt(0)),
              headers: { 'Content-Type': 'application/octet-stream' }
            });
            if (!uploadResp.ok) throw new Error(`Upload failed: ${uploadResp.status}`);
            progress.completed++;
          } catch (err) {
            progress.failed.push({ path: `${folderName}/${uploadUrl.path}`, error: err.message });
            progress.completed++;
          }
          setExecutionProgress({ ...progress });
        }

        // Upload thumbnail to media path if available
        if (newListing.thumbnailUploadUrl && newListing.thumbnailFile) {
          try {
            progress.current = `Uploading thumbnail for ${folderName}...`;
            setExecutionProgress({ ...progress });
            const thumbData = await window.electronAPI.readWorkflowFile(folderName, newListing.thumbnailFile);
            const thumbResp = await fetch(newListing.thumbnailUploadUrl, {
              method: 'PUT',
              body: Uint8Array.from(atob(thumbData.content), c => c.charCodeAt(0)),
              headers: { 'Content-Type': 'application/octet-stream' }
            });
            if (!thumbResp.ok) {
              console.warn(`[Sync] Thumbnail upload failed for ${folderName}: ${thumbResp.status}`);
            } else {
              console.log(`[Sync] Thumbnail uploaded for ${folderName}`);
            }
          } catch (err) {
            console.warn(`[Sync] Thumbnail upload error for ${folderName}:`, err.message);
          }
        }
      }

      // Download files from S3 to local
      for (const downloadUrl of (execResult.downloadUrls || [])) {
        try {
          progress.current = `Downloading ${downloadUrl.path}...`;
          setExecutionProgress({ ...progress });

          const op = operations.find(o => o.listingId === downloadUrl.listingId && o.type === 'download');
          const folderName = op?.localFolder;
          const fileSpec = op?.files?.find(f => f.path === downloadUrl.path);
          const localPath = fileSpec?.localPath || downloadUrl.path;

          if (folderName) {
            const resp = await fetch(downloadUrl.url);
            if (!resp.ok) throw new Error(`Download failed: ${resp.status}`);
            const blob = await resp.blob();
            const arrayBuffer = await blob.arrayBuffer();
            const base64 = btoa(String.fromCharCode(...new Uint8Array(arrayBuffer)));
            await window.electronAPI.writeWorkflowFile(folderName, localPath, base64);
          }
          progress.completed++;
        } catch (err) {
          progress.failed.push({ path: downloadUrl.path, error: err.message });
          progress.completed++;
        }
        setExecutionProgress({ ...progress });
      }

      // Clone downloads (cloud-only to local)
      for (const clone of (execResult.cloneDownloads || [])) {
        try {
          await window.electronAPI.createWorkflowFolder(clone.localFolder);
          for (const file of (clone.files || [])) {
            try {
              progress.current = `Downloading ${clone.localFolder}/${file.path}...`;
              setExecutionProgress({ ...progress });

              const resp = await fetch(file.url);
              if (!resp.ok) throw new Error(`Download failed: ${resp.status}`);
              const blob = await resp.blob();
              const arrayBuffer = await blob.arrayBuffer();
              const base64 = btoa(String.fromCharCode(...new Uint8Array(arrayBuffer)));
              await window.electronAPI.writeWorkflowFile(clone.localFolder, file.path, base64);
              progress.completed++;
            } catch (err) {
              progress.failed.push({ path: `${clone.localFolder}/${file.path}`, error: err.message });
              progress.completed++;
            }
            setExecutionProgress({ ...progress });
          }
        } catch (err) {
          progress.failed.push({ path: clone.localFolder, error: err.message });
        }
      }

      // 3. Finalize - update sync state
      progress.current = 'Saving sync state...';
      setExecutionProgress({ ...progress });

      const completedOps = [];

      // Build completion data for paired updates
      for (const op of operations) {
        if (op.type === 'upload' || op.type === 'download') {
          // Re-read the local manifest after sync to get current state
          let currentManifest;
          try {
            currentManifest = await window.electronAPI.getWorkflowManifest(op.localFolder);
          } catch {
            currentManifest = { files: [] };
          }
          completedOps.push({
            type: 'update',
            listingId: op.listingId,
            localFolder: op.localFolder,
            uploadedFiles: op.type === 'upload' ? op.files.map(f => f.path) : [],
            downloadedFiles: op.type === 'download' ? op.files.map(f => f.path) : [],
            currentManifest
          });
        } else if (op.type === 'create_listing') {
          const newListing = execResult.newListings?.find(n => n.localFolder === op.localFolder);
          if (newListing) {
            let currentManifest;
            try {
              currentManifest = await window.electronAPI.getWorkflowManifest(op.localFolder);
            } catch {
              currentManifest = { files: [] };
            }
            completedOps.push({
              type: 'new_listing',
              listingId: newListing.listingId,
              localFolder: op.localFolder,
              title: op.localFolder,
              currentManifest,
              // Thumbnail and icon metadata from local workflow
              thumbnailUrl: newListing.thumbnailUrl || null,
              iconName: op.iconName || null,
              iconWeight: op.iconWeight || null,
              iconColor: op.iconColor || null
            });
          }
        } else if (op.type === 'clone_to_local') {
          const clone = execResult.cloneDownloads?.find(c => c.listingId === op.listingId);
          if (clone) {
            let currentManifest;
            try {
              currentManifest = await window.electronAPI.getWorkflowManifest(clone.localFolder);
            } catch {
              currentManifest = { files: [] };
            }
            completedOps.push({
              type: 'clone_complete',
              listingId: op.listingId,
              localFolder: clone.localFolder,
              currentManifest
            });
          }
        }
      }

      if (completedOps.length > 0) {
        console.log('[Sync] Calling sync-complete with', completedOps.length, 'operations:', completedOps.map(o => `${o.type}:${o.localFolder || o.listingId}`));
        const completeToken = await getValidAccessToken();
        const completeResponse = await fetch(`${API_BASE}/marketplace-sync-complete`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${completeToken}`
          },
          body: JSON.stringify({ completedOperations: completedOps })
        });

        if (!completeResponse.ok) {
          const errData = await completeResponse.json().catch(() => ({}));
          const errMsg = errData.error || `Finalization failed (${completeResponse.status})`;
          console.error('[Sync] sync-complete error:', completeResponse.status, errMsg, errData);
          progress.failed.push({ path: 'sync-complete', error: errMsg });
        } else {
          const completeData = await completeResponse.json();
          console.log('[Sync] sync-complete result:', completeData);
          // Check individual operation results from backend
          if (completeData.results) {
            for (const r of completeData.results) {
              if (!r.success) {
                console.error('[Sync] Backend operation failed:', r);
                progress.failed.push({ path: r.localFolder || r.listingId || 'unknown', error: r.error || 'Operation failed on server' });
              }
            }
          }
        }
      } else {
        console.warn('[Sync] No completed operations to finalize');
      }

      console.log('[Sync] Finished:', { total: progress.total, succeeded: progress.completed - progress.failed.length, failed: progress.failed.length });

      setExecutionResults({
        total: progress.total,
        completed: progress.completed,
        failed: progress.failed
      });
      setPhase('complete');
      pendingNewListingIds.current = []; // Sync finished, nothing to clean up

      // Refresh the private exchange listings so user sees new uploads
      if (onSyncComplete) {
        onSyncComplete();
      }

    } catch (err) {
      console.error('[Sync] Execution error:', err);
      setError(err.message || 'Sync failed');
      setPhase('preview');
    }
  };

  if (!isOpen) return null;

  const formatSize = (bytes) => {
    if (!bytes) return '0 B';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const handleClose = async () => {
    // If closing mid-execution with pending new listings, clean up orphaned S3 files
    if (pendingNewListingIds.current.length > 0) {
      console.log('[Sync] Closing mid-sync, cleaning up', pendingNewListingIds.current.length, 'orphaned listings from S3');
      try {
        const token = await getValidAccessToken();
        await fetch(`${API_BASE}/marketplace-sync-cleanup`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({ listingIds: pendingNewListingIds.current })
        });
        pendingNewListingIds.current = [];
      } catch (err) {
        console.error('[Sync] Cleanup failed:', err);
      }
    }
    onClose();
  };

  const hasAnythingToSync = syncPlan && (
    syncPlan.paired?.some(p => p.summary.upload > 0 || p.summary.download > 0 || p.summary.conflict > 0) ||
    syncPlan.localOnly?.length > 0 ||
    syncPlan.cloudOnly?.length > 0
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm">
      <div className="bg-gray-900 border border-gray-700 rounded-xl w-full max-w-3xl max-h-[80vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-700">
          <div className="flex items-center gap-3">
            <RefreshCw className={`h-5 w-5 text-indigo-400 ${phase === 'scanning' || phase === 'executing' ? 'animate-spin' : ''}`} />
            <h2 className="text-lg font-semibold text-white">Sync with Local</h2>
          </div>
          <div className="flex items-center gap-4">
            {storageInfo && (
              <div className="text-xs text-gray-400">
                {formatSize(storageInfo.used)} / {formatSize(storageInfo.limit)} used
              </div>
            )}
            <button onClick={handleClose} className="text-gray-400 hover:text-white">
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          {/* Error */}
          {error && (
            <div className="mb-4 p-3 bg-red-900/30 border border-red-700/50 rounded-lg flex items-start gap-2">
              <AlertTriangle className="h-4 w-4 text-red-400 mt-0.5 flex-shrink-0" />
              <p className="text-red-200 text-sm">{error}</p>
            </div>
          )}

          {/* Scanning phase */}
          {phase === 'scanning' && (
            <div className="flex flex-col items-center justify-center py-16">
              <Loader2 className="h-8 w-8 text-indigo-400 animate-spin mb-4" />
              <p className="text-gray-300">Comparing local and cloud versions...</p>
              <p className="text-gray-500 text-sm mt-1">Checking for differences</p>
            </div>
          )}

          {/* Scan warnings */}
          {scanWarnings.length > 0 && phase === 'preview' && (
            <div className="mb-4 p-3 bg-amber-900/20 border border-amber-700/40 rounded-lg">
              <div className="flex items-start gap-2">
                <AlertTriangle className="h-4 w-4 text-amber-400 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-amber-200 text-sm font-medium">Some folders could not be scanned:</p>
                  {scanWarnings.map((w, i) => (
                    <p key={i} className="text-amber-400/70 text-xs mt-0.5">{w}</p>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Preview phase */}
          {phase === 'preview' && syncPlan && (
            <div className="space-y-3">
              {/* Bulk actions */}
              {hasAnythingToSync && (
                <div className="flex items-center justify-end gap-2 mb-4">
                  <button
                    onClick={executeUploadAll}
                    className="px-3 py-1.5 bg-indigo-700 hover:bg-indigo-600 text-white text-sm rounded-lg flex items-center gap-1.5 transition-colors"
                    title="Upload all local changes to Private Exchange"
                  >
                    <ArrowUp className="h-3.5 w-3.5" />
                    Upload All
                  </button>
                  <button
                    onClick={executeDownloadAll}
                    className="px-3 py-1.5 bg-emerald-700 hover:bg-emerald-600 text-white text-sm rounded-lg flex items-center gap-1.5 transition-colors"
                    title="Download all cloud changes from Exchange"
                  >
                    <ArrowDown className="h-3.5 w-3.5" />
                    Download All
                  </button>
                </div>
              )}

              {!hasAnythingToSync && (
                <div className="text-center py-12">
                  <Check className="h-10 w-10 text-emerald-400 mx-auto mb-3" />
                  <p className="text-gray-300 font-medium">Everything is up to date</p>
                  <p className="text-gray-500 text-sm mt-1">Your local workflows match the private exchange.</p>
                </div>
              )}

              {/* Paired workflows */}
              {syncPlan.paired?.map(paired => {
                const isExpanded = expandedWorkflows.has(paired.listingId);
                const hasChanges = paired.summary.upload > 0 || paired.summary.download > 0 || paired.summary.conflict > 0;

                if (!hasChanges) {
                  return (
                    <div key={paired.listingId} className="bg-gray-800/50 rounded-lg overflow-hidden">
                      <div className="flex items-center justify-between px-4 py-3 cursor-pointer" onClick={() => toggleExpanded(paired.listingId)}>
                        <div className="flex items-center gap-3">
                          <span className="text-gray-400">
                            {isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                          </span>
                          <Check className="h-4 w-4 text-emerald-400" />
                          <span className="text-gray-300 text-sm font-medium">{paired.cloudTitle || paired.localFolder}</span>
                        </div>
                        <span className="text-gray-500 text-xs">Up to date</span>
                      </div>
                      {isExpanded && (
                        <div className="border-t border-gray-700/50 px-4 py-2 space-y-1">
                          {paired.actions.map(action => (
                            <div key={action.path} className="flex items-center justify-between py-1.5 px-2 rounded">
                              <div className="flex items-center gap-2 min-w-0">
                                <Check className="h-3 w-3 text-emerald-400/50 flex-shrink-0" />
                                <span className="text-gray-400 text-xs truncate">{action.path}</span>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                }

                return (
                  <div key={paired.listingId} className="bg-gray-800/50 rounded-lg border border-gray-700/50 overflow-hidden">
                    {/* Workflow row */}
                    <div className="flex items-center justify-between px-4 py-3">
                      <div className="flex items-center gap-3">
                        <button onClick={() => toggleExpanded(paired.listingId)} className="text-gray-400 hover:text-white">
                          {isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                        </button>
                        {paired.summary.conflict > 0 ? (
                          <AlertTriangle className="h-4 w-4 text-amber-400" />
                        ) : (
                          <RefreshCw className="h-4 w-4 text-indigo-400" />
                        )}
                        <span className="text-gray-200 text-sm font-medium">{paired.cloudTitle || paired.localFolder}</span>
                        <span className="text-gray-500 text-xs">
                          {paired.summary.upload > 0 && `${paired.summary.upload} to upload`}
                          {paired.summary.upload > 0 && paired.summary.download > 0 && ', '}
                          {paired.summary.download > 0 && `${paired.summary.download} to download`}
                          {paired.summary.conflict > 0 && ` (${paired.summary.conflict} conflict${paired.summary.conflict > 1 ? 's' : ''})`}
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        {paired.summary.upload > 0 && (
                          <button
                            onClick={() => executeUpload(paired)}
                            disabled={hasUnresolvedConflicts(paired)}
                            className="p-1.5 bg-indigo-700 hover:bg-indigo-600 disabled:bg-gray-700 disabled:cursor-not-allowed text-white rounded transition-colors"
                            title="Upload to Private Exchange"
                          >
                            <ArrowUp className="h-3.5 w-3.5" />
                          </button>
                        )}
                        {paired.summary.download > 0 && (
                          <button
                            onClick={() => executeDownload(paired)}
                            disabled={hasUnresolvedConflicts(paired)}
                            className="p-1.5 bg-emerald-700 hover:bg-emerald-600 disabled:bg-gray-700 disabled:cursor-not-allowed text-white rounded transition-colors"
                            title="Download from Exchange"
                          >
                            <ArrowDown className="h-3.5 w-3.5" />
                          </button>
                        )}
                      </div>
                    </div>

                    {/* Expanded file details */}
                    {isExpanded && (
                      <div className="border-t border-gray-700/50 px-4 py-2 space-y-1">
                        {paired.actions.filter(a => a.action !== 'unchanged' && a.action !== 'unchanged_probable').map(action => (
                          <div key={action.path} className="flex items-center justify-between py-1.5 px-2 rounded hover:bg-gray-800/50">
                            <div className="flex items-center gap-2 min-w-0">
                              {action.action === 'upload' && <ArrowUp className="h-3 w-3 text-indigo-400 flex-shrink-0" />}
                              {action.action === 'download' && <ArrowDown className="h-3 w-3 text-emerald-400 flex-shrink-0" />}
                              {action.action === 'conflict' && <AlertTriangle className="h-3 w-3 text-amber-400 flex-shrink-0" />}
                              <span className="text-gray-300 text-xs truncate">{action.path}</span>
                            </div>
                            <div className="flex items-center gap-2 flex-shrink-0">
                              {action.action === 'upload' && (
                                <span className="text-indigo-400 text-xs">{formatSize(action.localSize)}</span>
                              )}
                              {action.action === 'download' && (
                                <span className="text-emerald-400 text-xs">{formatSize(action.cloudSize)}</span>
                              )}
                              {action.action === 'conflict' && (
                                <div className="flex items-center gap-1">
                                  <span className="text-amber-400/70 text-xs mr-2">
                                    {action.localSize ? formatSize(action.localSize) : ''} vs {action.cloudSize ? formatSize(action.cloudSize) : ''}
                                  </span>
                                  {(() => {
                                    const resolution = conflictResolutions[paired.listingId]?.[action.path];
                                    return (
                                      <>
                                        <button
                                          onClick={() => resolveConflict(paired.listingId, action.path, 'keep_local')}
                                          className={`px-1.5 py-0.5 text-[10px] rounded ${
                                            resolution === 'keep_local' ? 'bg-indigo-600 text-white' : 'bg-gray-700 text-gray-400 hover:bg-gray-600'
                                          }`}
                                        >
                                          Keep Mine
                                        </button>
                                        <button
                                          onClick={() => resolveConflict(paired.listingId, action.path, 'keep_cloud')}
                                          className={`px-1.5 py-0.5 text-[10px] rounded ${
                                            resolution === 'keep_cloud' ? 'bg-emerald-600 text-white' : 'bg-gray-700 text-gray-400 hover:bg-gray-600'
                                          }`}
                                        >
                                          Keep Cloud
                                        </button>
                                        <button
                                          onClick={() => resolveConflict(paired.listingId, action.path, 'keep_both')}
                                          className={`px-1.5 py-0.5 text-[10px] rounded ${
                                            resolution === 'keep_both' ? 'bg-amber-600 text-white' : 'bg-gray-700 text-gray-400 hover:bg-gray-600'
                                          }`}
                                        >
                                          Both
                                        </button>
                                      </>
                                    );
                                  })()}
                                </div>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}

              {/* Local-only workflows */}
              {syncPlan.localOnly?.map(local => (
                <div key={local.localFolder} className="flex items-center justify-between px-4 py-3 bg-gray-800/50 rounded-lg border border-gray-700/50">
                  <div className="flex items-center gap-3">
                    <ArrowUp className="h-4 w-4 text-indigo-400" />
                    <span className="text-gray-200 text-sm font-medium">{local.localFolder}</span>
                    <span className="text-gray-500 text-xs">Local only &middot; {local.fileCount} files &middot; {formatSize(local.totalSize)}</span>
                  </div>
                  <button
                    onClick={() => executeUploadNew(local)}
                    className="p-1.5 bg-indigo-700 hover:bg-indigo-600 text-white rounded transition-colors"
                    title="Upload to Private Exchange"
                  >
                    <ArrowUp className="h-3.5 w-3.5" />
                  </button>
                </div>
              ))}

              {/* Cloud-only workflows */}
              {syncPlan.cloudOnly?.map(cloud => (
                <div key={cloud.listingId} className="flex items-center justify-between px-4 py-3 bg-gray-800/50 rounded-lg border border-gray-700/50">
                  <div className="flex items-center gap-3">
                    <ArrowDown className="h-4 w-4 text-emerald-400" />
                    <span className="text-gray-200 text-sm font-medium">{cloud.title}</span>
                    <span className="text-gray-500 text-xs">Cloud only &middot; {cloud.fileCount} files &middot; {formatSize(cloud.totalSize)}</span>
                  </div>
                  <button
                    onClick={() => executeDownloadNew(cloud)}
                    className="p-1.5 bg-emerald-700 hover:bg-emerald-600 text-white rounded transition-colors"
                    title="Download from Exchange"
                  >
                    <ArrowDown className="h-3.5 w-3.5" />
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* Executing phase */}
          {phase === 'executing' && executionProgress && (
            <div className="flex flex-col items-center justify-center py-12">
              <Loader2 className="h-8 w-8 text-indigo-400 animate-spin mb-4" />
              <p className="text-gray-300 font-medium">Syncing...</p>
              <p className="text-gray-500 text-sm mt-1">{executionProgress.current}</p>
              <div className="w-64 mt-4 bg-gray-800 rounded-full h-2">
                <div
                  className="bg-indigo-500 h-2 rounded-full transition-all"
                  style={{ width: `${executionProgress.total > 0 ? (executionProgress.completed / executionProgress.total * 100) : 0}%` }}
                />
              </div>
              <p className="text-gray-500 text-xs mt-2">{executionProgress.completed} / {executionProgress.total}</p>
              {executionProgress.failed.length > 0 && (
                <p className="text-red-400 text-xs mt-2">{executionProgress.failed.length} error{executionProgress.failed.length !== 1 ? 's' : ''} so far</p>
              )}
            </div>
          )}

          {/* Complete phase */}
          {phase === 'complete' && executionResults && (
            <div className="flex flex-col items-center justify-center py-12">
              {executionResults.failed.length === 0 ? (
                <Check className="h-10 w-10 text-emerald-400 mb-4" />
              ) : executionResults.failed.length >= executionResults.total ? (
                <AlertTriangle className="h-10 w-10 text-red-400 mb-4" />
              ) : (
                <AlertTriangle className="h-10 w-10 text-amber-400 mb-4" />
              )}
              <p className="text-gray-200 font-medium">
                {executionResults.failed.length === 0
                  ? 'Sync complete!'
                  : executionResults.failed.length >= executionResults.total
                    ? 'Sync failed'
                    : 'Sync completed with errors'
                }
              </p>
              <p className="text-gray-500 text-sm mt-1">
                {executionResults.failed.length >= executionResults.total
                  ? 'All operations failed - check the errors below'
                  : <>
                      {executionResults.completed - executionResults.failed.length} files synced successfully
                      {executionResults.failed.length > 0 && `, ${executionResults.failed.length} failed`}
                    </>
                }
              </p>

              {executionResults.failed.length > 0 && (
                <div className="mt-4 w-full max-w-md space-y-1">
                  {executionResults.failed.map((f, i) => (
                    <div key={i} className="flex items-start gap-2 text-xs bg-red-900/20 border border-red-800/30 rounded px-3 py-2">
                      <AlertTriangle className="h-3 w-3 text-red-400 mt-0.5 flex-shrink-0" />
                      <div>
                        <span className="text-red-300">{f.path}</span>
                        <span className="text-red-400/70 ml-2">{f.error}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              <div className="flex items-center gap-3 mt-6">
                <button
                  onClick={() => {
                    scanAndCompare();
                  }}
                  className="px-4 py-2 bg-gray-800 hover:bg-gray-700 text-gray-300 text-sm rounded-lg transition-colors"
                >
                  Scan Again
                </button>
                <button
                  onClick={handleClose}
                  className="px-4 py-2 bg-indigo-700 hover:bg-indigo-600 text-white text-sm rounded-lg transition-colors"
                >
                  Done
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SyncPanel;
